import { getCookie } from "@commonUtils"
import WVKit from "../webviewkit"
import { legacyHandler } from "@tata1mg/synapse/handler"
import { checkVersionSupport } from "@tata1mg/synapse/utils"
import { SUPPORTED_VERSIONS_FOR_WEBVIEW_SUPER_PROPERTIES_CALLBACK } from "@constants"

/**
 * Method used for GA tracking
 * @param {object} targetObject - current question that's clicked
 * @param {object} data - event action, label and category
 * @memberOf Questions
 */

/**
 ** Added a Synapse handler to enable Mixpanel tracking for dWeb URLs in WebView.
 *
 * - In the `shell-start` file, we check `isWebview` to prevent Rudder analytics from tracking dWeb URLs in WebView.
 * - However, a new requirement emerged where dWeb URLs need to be opened within WebView, requiring Mixpanel tracking.
 * - To address this, we've introduced Synapse handler to track Mixpanel events for dWeb URLs in WebView.
 */

const isStandAlone = () => !!window?.matchMedia("(display-mode: standalone)").matches

export function pushToGTM(eventObj, eventData = {}) {
    let tataCustomerHash
    if (typeof window !== "undefined") {
        tataCustomerHash = getCookie("tata_customer_hash")
    }
    const { label, action, category, eventName, transactionTotal, newUser } = eventObj
    const data = {
        eventAction: action,
        eventLabel: label,
        eventCategory: category,
        event: eventName,
        tataCustomerHash: tataCustomerHash,
    }
    // For transaction related events we need to send the transaction amount
    if (transactionTotal) data.transactionTotal = transactionTotal
    // For transaction related events of New Users
    if (newUser) data.newUser = newUser
    // For pageViewActions rudder event is not required by the Analytics team, GA still needed to be sent here.
    const skipRudderTracking = action === "Page View Action"
    const skipGATracking = eventData?.skipGATracking || false

    if (window.dataLayer) {
        if (!skipGATracking) {
            window.dataLayer.push(data)
            window.dataLayer.splice(0, 1, { ...window.dataLayer[0], tataCustomerHash })
        }
        const visitor_id =
            getCookie("VISITOR-ID") || window.dataLayer?.[0]?.visitorId || window.visitorId || ""

        if (!skipRudderTracking) {
            if (window.rudderanalytics && window.rudderanalytics.track) {
                const device = "desktop"
                const isPWA = !!isStandAlone()
                window.rudderanalytics.track(action, {
                    category,
                    label,
                    device,
                    isPWA,
                    visitor_id,
                    ...eventData,
                    tataCustomerHash,
                })
            }
        }

        // WebView analytics interface call
        let analyticsType = "all"
        if (skipGATracking) analyticsType = "rudder"
        else if (skipRudderTracking) analyticsType = "ga"
        if (eventData?.sendSingular) analyticsType = "singular"
        const _data = {
            analyticsType: analyticsType,
            type: "track",
            event: action,
            category: category,
            label: typeof label === "string" ? label : JSON.stringify(label),
            properties: { visitor_id, ...eventData, tataCustomerHash, user_view: "webview" },
        }
        legacyHandler({
            current: () => window.synapse.AppCallbacks.analytics[analyticsType](_data),
            legacy: () => WVKit.call("analytics", _data),
        })
    }
}

export const resetDataLayer = () => {
    if (
        typeof window.dataLayer !== "undefined" &&
        typeof window.google_tag_manager !== "undefined" &&
        typeof window.google_tag_manager[process.env.GTM_ID] !== "undefined"
    ) {
        if (typeof window.google_tag_manager[process.env.GTM_ID].dataLayer !== "undefined") {
            window.google_tag_manager[process.env.GTM_ID].dataLayer.reset()
            return true
        } else {
            return false
        }
    }
}

export const pushToDataLayer = (dimensions, additionalData = {}) => {
    if (typeof window.dataLayer !== "undefined" && Array.isArray(window.dataLayer)) {
        let tataCustomerHash
        tataCustomerHash = getCookie("tata_customer_hash")
        const deviceInfo = {}
        const user_id = getCookie("userId") || ""
        const corpDetails = getCookie("corporateDetails") || {}
        const cod_locked_state = getCookie("cod_locked_state") || false

        const corporateDetails = {
            is_corporate_user: corpDetails._source ? true : false,
            corporate_name: corpDetails._source ? corpDetails._source : "null",
            has_corporate_dashboard: corpDetails._redirect_url ? true : false,
        }

        if (navigator?.connection) {
            deviceInfo.networkType = navigator.connection.effectiveType
            deviceInfo.downlink = navigator.connection.downlink
            deviceInfo.rtt = navigator.connection.rtt
        }

        if (navigator?.hardwareConcurrency) {
            deviceInfo.cpuCores = navigator.hardwareConcurrency
        }

        window.dataLayer.push(dimensions)
        if (window.rudderanalytics && window.rudderanalytics.track) {
            window.dataLayer.splice(0, 1, { ...window.dataLayer[0], tataCustomerHash })
            const device = "desktop"
            let rudderanalytics = window.rudderanalytics
            let { page, visitorId } = dimensions
            rudderanalytics.page(page, `${page} PWA page visited.`, {
                datalayer: {
                    ...dimensions,
                    ...deviceInfo,
                    visitorId:
                        visitorId ||
                        getCookie("VISITOR-ID") ||
                        window.dataLayer?.[0]?.visitorId ||
                        window.visitorId,
                    user_id: user_id,
                    corporateDetails,
                    cod_locked_state,
                    event: "gtm.js",
                    is_pwa: true,
                    tataCustomerHash: getCookie("tata_customer_hash") || "",
                },
                device,
                path: location.pathname,
                ...additionalData,
            })
        }

        // WebView analytics interface call
        const data = {
            analyticsType: "all",
            type: "page",
            event: `${dimensions.page} PWA page visited.`,
            category: dimensions.page,
            label: typeof dimensions.page === "string" ? dimensions.page : JSON.stringify(dimensions.page),
            properties: { ...dimensions, ...additionalData, user_view: "webview" },
        }
        legacyHandler({
            current: () => window.synapse.AppCallbacks.analytics.all(data),
            legacy: () => WVKit.call("analytics", data),
        })
    }
}

export function rudderTrackEvent(label, action, category, data = {}) {
    if (typeof window !== "undefined" && window.rudderanalytics && window.rudderanalytics.track) {
        const rudderData = {
            category,
            label,
            ...data,
        }
        window.rudderanalytics.track(action, rudderData)
    }

    const _data = {
        analyticsType: "rudder",
        type: "track",
        event: action,
        category: category,
        label: typeof label === "string" ? label : JSON.stringify(label),
        properties: { ...data, user_view: "webview" },
    }
    legacyHandler({
        current: () => window.synapse.AppCallbacks.analytics.rudder(_data),
        legacy: () => WVKit.call("analytics", _data),
    })
}

const getReferrerDomain = (referrer) => {
    try {
        return new URL(referrer)?.hostname
    } catch (error) {
        return ""
    }
}

const giveCorporateMixpanelGlobalProperties = () => {
    return {
        unique_userid: getCookie("userId") || "",
        is_logged_in: !!getCookie("loggedIn"),
        city: decodeURI(getCookie("city")),
        pincode: getCookie("pincode"),
    }
}

const giveMixpanelGlobalProperties = () => {
    return {
        unique_userid: getCookie("userId") || "",
        is_logged_in: !!getCookie("loggedIn"),
        city: decodeURI(getCookie("city")),
        cod_locked_state: getCookie("cod_locked_state") || false,
        cp_added_source: getCookie("cp_added_source") || null,
        polygon_id: getCookie("polygonIDs"),
        pincode_selected: getCookie("pincode"),
        demand_cluster_id: getCookie("demandClusterId"),
        address_type: getCookie("addressType"),
        is_cp_member: getCookie("is_cp_member") || false,
        visitorid: getCookie("VISITOR-ID"),
    }
}

const getWebviewRudderData = (commonRudderData) => {
    const setPropertyCallbackPresentInNative =
        checkVersionSupport({
            android: SUPPORTED_VERSIONS_FOR_WEBVIEW_SUPER_PROPERTIES_CALLBACK.android,
        }) ||
        checkVersionSupport({
            ios: SUPPORTED_VERSIONS_FOR_WEBVIEW_SUPER_PROPERTIES_CALLBACK.ios,
        })
    return {
        ...commonRudderData,
        is_webview: true,
        ...(setPropertyCallbackPresentInNative ? {} : giveMixpanelGlobalProperties()),
    }
}

const getDocumentReferrer = () => {
    const initialReferer = document.referrer === "" ? "$direct" : document.referrer
    const currentEntryIndex = window.history?.state?.idx
    const allEntries = JSON.parse(sessionStorage.getItem("appHistory")) || []
    return currentEntryIndex > 0 ? allEntries[currentEntryIndex - 1]?.url : initialReferer
}

export function sendMixpanelPageEvent(name, properties, isCorporate = false) {
    const referrer = getDocumentReferrer()
    const rudderData = {
        event_structure: isCorporate ? "mixpanel_cwp" : "mixpanel",
        ...(isCorporate ? giveCorporateMixpanelGlobalProperties() : giveMixpanelGlobalProperties()),
        url: location.href,
        referrer,
        referring_domain: referrer === "$direct" ? "" : getReferrerDomain(referrer),
        ...properties,
        path: location.pathname,
    }

    // Check if running in browser environment and if Rudder Analytics is available
    if (typeof window !== "undefined" && window.rudderanalytics && window.rudderanalytics.page) {
        window.rudderanalytics.page(name, { ...rudderData })
    }

    // WebView analytics interface call
    const _data = {
        analyticsType: "mixpanel",
        type: "page",
        event: name,
        properties: getWebviewRudderData(rudderData),
    }

    legacyHandler({
        current: () => window.synapse.AppCallbacks.analytics.mixpanel(_data),
        legacy: () => WVKit.call("analytics", _data),
    })
}

export function sendCorporateMixpanelPageEvent(name, properties) {
    sendMixpanelPageEvent(name, properties, true)
}

export function sendMixpanelResetEvent() {
    // Check if running in browser environment and if Rudder Analytics is available
    if (typeof window !== "undefined" && window.rudderanalytics && window.rudderanalytics.reset) {
        window.rudderanalytics.reset(true)
    }

    // WebView analytics interface call
    const _data = {
        analyticsType: "rudder",
        type: "reset",
    }
    legacyHandler({
        current: () => window.synapse.AppCallbacks.analytics.rudder(_data),
        legacy: () => WVKit.call("analytics", _data),
    })
}

export function sendMixpanelTrackEvent(event, properties, isCorporate = false) {
    const rudderData = {
        event_structure: isCorporate ? "mixpanel_cwp" : "mixpanel",
        ...(isCorporate ? giveCorporateMixpanelGlobalProperties() : giveMixpanelGlobalProperties()),
        ...properties,
    }

    // Check if running in browser environment and if Rudder Analytics is available
    if (typeof window !== "undefined" && window.rudderanalytics && window.rudderanalytics.track) {
        window.rudderanalytics.track(event, { ...rudderData })
    }

    // WebView analytics interface call
    const _data = {
        analyticsType: "mixpanel",
        type: "track",
        event,
        properties: getWebviewRudderData(rudderData),
    }

    legacyHandler({
        current: () => window.synapse.AppCallbacks.analytics.mixpanel(_data),
        legacy: () => WVKit.call("analytics", _data),
    })
}

export function sendCorporateMixpanelTrackEvent(event, properties) {
    sendMixpanelTrackEvent(event, properties, true)
}
